import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Save, ArrowLeft, AlertCircle, Info, GripVertical } from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_conversion_rule,
  get_view_units,
} from "../ServiceConnection/serviceconnection";
import Loader from "../components/layout/Loader";

export default function AddUnitConversionRule() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const currentUrl = location.pathname.substring(1);

  const [showLoaderAdmin, setshowLoaderAdmin] = useState(false);
  const [baseunit, setbaseunit] = useState("");
  const [baseunit_id, setbaseunit_id] = useState("");
  const [baseunitshortname, setbaseunitshortname] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [materials, setMaterials] = useState([
    {
      id: Date.now(),
      primary_id: "",
      unit_name: "",
      min_value: 0,
      max_value: "",
      order: 0,
    },
  ]);

  // Helper: empty check
  const isEmpty = (v) =>
    v === "" ||
    v === null ||
    v === undefined ||
    String(v).trim() === "" ||
    isNaN(Number(v));

  // Load initial data
  useEffect(() => {
    const parts = currentUrl.split("/");
    const call_id = parts.length !== 1 ? parts[1] : "0";
    setbaseunit_id(call_id);
    loadMaster(call_id);
  }, []);

  const loadMaster = async (call_id) => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("start_date", "");
    fd.append("end_date", "");
    fd.append("flag", "3");
    fd.append("call_id", call_id);

    try {
      const res = await server_post_data(get_view_units, fd);

      if (res.data.error) {
        toast({
          title: "Error",
          description: res.data.message,
          variant: "destructive",
        });
        return;
      }

      let rows = res.data.message.map((row, i) => ({
        ...row,
        id: row.primary_id,
        order: i,
      }));

      setMaterials(rows);

      rows.forEach((r) => {
        if (r.base_unit_id === 0) {
          setbaseunit(r.unit_name);
          setbaseunitshortname(r.short_code_name);
        }
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Network error",
        variant: "destructive",
      });
    } finally {
      setshowLoaderAdmin(false);
    }
  };

  // VALIDATION
  const validateField = (rowId, field, value, rows) => {
    const errors = { ...validationErrors };
    const idx = rows.findIndex((r) => r.id === rowId);
    const row = rows[idx];

    const prevMax = idx > 0 ? Number(rows[idx - 1].max_value) : null;

    // Clear old
    if (errors[rowId]?.[field]) {
      delete errors[rowId][field];
      if (Object.keys(errors[rowId]).length === 0) delete errors[rowId];
    }

    // MIN VALIDATION
    if (field === "min_value") {
      const requiredMin = idx === 0 ? 0 : prevMax + 1;

      if (isEmpty(value)) {
        errors[rowId] = errors[rowId] || {};
        errors[rowId].min_value = "Minimum value is required";
        setValidationErrors(errors);
        return false;
      }

      const numVal = Number(value);

      if (idx !== 0 && numVal === 0) {
        errors[rowId] = errors[rowId] || {};
        errors[rowId].min_value = "Minimum value cannot be 0";
        setValidationErrors(errors);
        return false;
      }

      if (numVal !== requiredMin) {
        errors[rowId] = errors[rowId] || {};
        errors[rowId].min_value = `Minimum must be exactly ${requiredMin}`;
        setValidationErrors(errors);
        return false;
      }
    }

    // MAX VALIDATION
    if (field === "max_value") {
      if (isEmpty(value)) {
        errors[rowId] = errors[rowId] || {};
        errors[rowId].max_value = "Maximum value is required";
        setValidationErrors(errors);
        return false;
      }

      const numVal = Number(value);
      const minVal = Number(row.min_value);

      if (numVal === 0) {
        errors[rowId] = errors[rowId] || {};
        errors[rowId].max_value = "Maximum value cannot be 0";
        setValidationErrors(errors);
        return false;
      }

      if (numVal <= minVal) {
        errors[rowId] = errors[rowId] || {};
        errors[rowId].max_value = "Maximum must be greater than Minimum";
        setValidationErrors(errors);
        return false;
      }
    }

    setValidationErrors(errors);
    return true;
  };

  // INPUT CHANGE HANDLER
  const handleInputChange = (id, field, value) => {
    setMaterials((prev) => {
      const updated = prev.map((r) =>
        r.id === id ? { ...r, [field]: value } : r,
      );

      // Auto calc next min
      if (field === "max_value") {
        const idx = updated.findIndex((r) => r.id === id);
        if (idx + 1 < updated.length) {
          updated[idx + 1].min_value = Number(value) + 1;
        }
      }

      setTimeout(() => validateField(id, field, value, updated));

      return updated;
    });
  };

  // Mandatory validation before submit
  const validateBeforeSubmit = () => {
    let valid = true;
    const errors = {};

    materials.forEach((row, idx) => {
      // MIN mandatory
      if (
        isEmpty(row.min_value) ||
        (idx !== 0 && Number(row.min_value) === 0)
      ) {
        errors[row.id] = errors[row.id] || {};
        errors[row.id].min_value = "Minimum value is required";
        valid = false;
      }

      // MAX mandatory
      if (isEmpty(row.max_value) || Number(row.max_value) === 0) {
        errors[row.id] = errors[row.id] || {};
        errors[row.id].max_value = "Maximum value is required";
        valid = false;
      }
    });

    setValidationErrors(errors);
    return valid;
  };

  // SUBMIT HANDLER
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateBeforeSubmit()) {
      toast({
        title: "Validation Error",
        description: "Please fix the validation errors.",
        variant: "destructive",
      });
      return;
    }

    setshowLoaderAdmin(true);

    const fd = new FormData();

    // Base unit info
    fd.append("base_unit_id", baseunit_id);
    fd.append("base_unit_name", baseunit);

    // Append values in table order
    materials.forEach((row) => {
      fd.append("unit_id[]", row.primary_id);
      fd.append("min_value[]", row.min_value);
      fd.append("max_value[]", row.max_value);
      fd.append("unit_name[]", row.unit_name);
    });

    try {
      const res = await server_post_data(save_update_conversion_rule, fd);

      if (res.data.error) {
        toast({
          title: "Error",
          description: res.data.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Conversion rules updated successfully!",
      });

      // Reload data
      loadMaster(baseunit_id);
    } catch (error) {
      toast({
        title: "Error",
        description: "Network error",
        variant: "destructive",
      });
    } finally {
      setshowLoaderAdmin(false);
    }
  };

  return (
    <MainLayout>
      <Header
        title="Unit Conversion Rules"
        subtitle={`Configure conversion rules for ${baseunit || "unit"}`}
      />

      <div className="p-6 space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/view-units")}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Units
        </Button>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Info Alert */}
          <Alert className="bg-cyan-50 border-cyan-200">
            <Info className="h-4 w-4 text-cyan-600" />
            <AlertDescription className="text-cyan-800">
              <strong>
                Base Unit: {baseunit} ({baseunitshortname})
              </strong>
              <br />
              Define the min and max ranges for each unit in the conversion
              hierarchy. The minimum value for each unit automatically starts
              where the previous unit's maximum ends.
            </AlertDescription>
          </Alert>

          {/* Conversion Rules Table */}
          <Card>
            <CardHeader>
              <CardTitle>Conversion Rules Configuration</CardTitle>
              <CardDescription>
                Set the range boundaries for automatic unit conversion
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-[60px]">Order</TableHead>
                      <TableHead>Unit Name</TableHead>
                      <TableHead>Min ({baseunitshortname})</TableHead>
                      <TableHead>Max ({baseunitshortname})</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materials.map((item, index) => {
                      const prevMax =
                        index > 0
                          ? Number(materials[index - 1].max_value)
                          : null;

                      const minIsEditable =
                        index !== 0 &&
                        (isEmpty(item.min_value) ||
                          Number(item.min_value) !== prevMax + 1);

                      return (
                        <TableRow key={item.id}>
                          {/* Order */}
                          <TableCell className="text-center">
                            <GripVertical className="w-4 h-4 mx-auto text-muted-foreground" />
                          </TableCell>

                          {/* Unit Name */}
                          <TableCell>
                            <Input
                              value={item.unit_name}
                              readOnly
                              className="bg-muted/30 border-0"
                            />
                          </TableCell>

                          {/* Min Value */}
                          <TableCell>
                            <div className="space-y-1">
                              <Input
                                type="number"
                                value={
                                  isEmpty(item.min_value) ? "" : item.min_value
                                }
                                readOnly={!minIsEditable}
                                placeholder={
                                  index !== 0 ? `Must be ${prevMax + 1}` : "0"
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    item.id,
                                    "min_value",
                                    e.target.value,
                                  )
                                }
                                className={
                                  validationErrors[item.id]?.min_value
                                    ? "border-destructive"
                                    : ""
                                }
                              />
                              {validationErrors[item.id]?.min_value && (
                                <p className="text-xs text-destructive flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {validationErrors[item.id].min_value}
                                </p>
                              )}
                            </div>
                          </TableCell>

                          {/* Max Value */}
                          <TableCell>
                            <div className="space-y-1">
                              <Input
                                type="number"
                                value={item.max_value || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    item.id,
                                    "max_value",
                                    e.target.value,
                                  )
                                }
                                className={
                                  validationErrors[item.id]?.max_value
                                    ? "border-destructive"
                                    : ""
                                }
                              />
                              {validationErrors[item.id]?.max_value && (
                                <p className="text-xs text-destructive flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {validationErrors[item.id].max_value}
                                </p>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Summary Card */}
          <Card className="border-cyan-200 bg-cyan-50/30">
            <CardHeader>
              <CardTitle className="text-lg">Conversion Summary</CardTitle>
              <CardDescription>
                Preview of the conversion rules you've configured
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {materials.map((item, idx) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-semibold text-sm">
                        {idx + 1}
                      </div>
                      <span className="font-medium">{item.unit_name}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <span className="font-mono font-medium text-foreground">
                        {isEmpty(item.min_value) ? "-" : item.min_value}
                      </span>
                      {" - "}
                      <span className="font-mono font-medium text-foreground">
                        {isEmpty(item.max_value) ? "-" : item.max_value}
                      </span>{" "}
                      {baseunitshortname}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/view-units")}
              disabled={showLoaderAdmin}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={showLoaderAdmin}
              className="gap-2 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white"
            >
              {showLoaderAdmin ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Conversion Rules
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
      {showLoaderAdmin && <Loader />}
    </MainLayout>
  );
}
